import type {
  EventEntry,
  GameObjectWithId,
  Inventory,
  ItemWithId,
  PlayerStance,
  PublicPlayer,
  TemporaryStatus,
} from './backend';
import type { Principal } from '@dfinity/principal';
import { enumIs } from './enums';
import { unreachable } from './unreachable';
import {
  type Particle,
  BulletParticle,
  HealthChangeParticle,
  ExplosionParticle,
} from './particles';
import type { PlayerState } from './game';
import { assets, playSound } from './assets';
import type { Messages } from './messages';
import { getItemName } from './items';

const processedEvents = new Set<any>();

export function processGameEvents(
  events: EventEntry[],
  publicPlayers: PublicPlayer[],
  messages: Messages
) {
  for (const event of events) {
    (event as any).processed = ((event as any).processed || 0) + 1;

    if (enumIs(event.data, 'Game')) {
      if (processedEvents.has(event)) {
        continue;
      }
      processedEvents.add(event);
      if (enumIs(event.data.Game, 'Kill')) {
        const { killer, victim } = event.data.Game.Kill;
        const killerPlayer = publicPlayers.find(
          (p) => p.principal.compareTo(killer) == 'eq'
        );
        if (killerPlayer) killerPlayer.kills++;
        const victimPlayer = publicPlayers.find(
          (p) => p.principal.compareTo(victim) == 'eq'
        );
        if (victimPlayer) victimPlayer.dead = true;

        if (killerPlayer && victimPlayer) {
          messages.addMessage(
            `${killerPlayer.name} killed ${victimPlayer.name}!`
          );
        }
      } else if (enumIs(event.data.Game, 'Death')) {
        const { player, cause } = event.data.Game.Death;
        const victimPlayer = publicPlayers.find(
          (p) => p.principal.compareTo(player) == 'eq'
        );

        if (victimPlayer) {
          victimPlayer.dead = true;
          messages.addMessage(`${victimPlayer.name} killed by ${cause}!`);
        }
      } else if (enumIs(event.data.Game, 'PlayerLeave')) {
        const { player } = event.data.Game.PlayerLeave;
        const victimPlayer = publicPlayers.find(
          (p) => p.principal.compareTo(player) == 'eq'
        );
        if (victimPlayer) {
          victimPlayer.dead = true;
          messages.addMessage(`${victimPlayer.name} left the game.`);
        }
      } else {
        unreachable(event.data.Game);
      }
    }
  }
}

export function processItemEvents(
  events: EventEntry[],
  messages: Messages
): ItemWithId[] {
  const items: ItemWithId[] = [];

  for (const event of events) {
    if (!enumIs(event.data, 'Item')) {
      continue;
    }

    (event as any).processed = ((event as any).processed || 0) + 1;

    if (enumIs(event.data.Item.event, 'OnGround')) {
      items.push(event.data.Item.item);
    } else if (enumIs(event.data.Item.event, 'Improved')) {
      if (processedEvents.has(event)) {
        continue;
      }
      processedEvents.add(event);

      if (enumIs(event.data.Item.item.item_type, 'Armor')) {
        messages.addMessage(
          `Your armor has been improved to level ${event.data.Item.item.item_type.Armor.level}!`
        );
        playSound(assets.sounds.itemImproved);
      } else if (enumIs(event.data.Item.item.item_type, 'Boots')) {
        messages.addMessage(
          `Your boots have been improved to level ${event.data.Item.item.item_type.Boots.level}!`
        );
        playSound(assets.sounds.itemImproved);
      } else if (enumIs(event.data.Item.item.item_type, 'EnergyDrink')) {
      } else if (enumIs(event.data.Item.item.item_type, 'Grenade')) {
      } else if (enumIs(event.data.Item.item.item_type, 'Knife')) {
        messages.addMessage(
          `Your knife has been improved to level ${event.data.Item.item.item_type.Knife.level}!`
        );
        playSound(assets.sounds.itemImproved);
      } else if (enumIs(event.data.Item.item.item_type, 'MedKit')) {
      } else if (enumIs(event.data.Item.item.item_type, 'Pistol')) {
        messages.addMessage(
          `Your pistol has been improved to level ${event.data.Item.item.item_type.Pistol.level}!`
        );
        playSound(assets.sounds.itemImproved);
      } else {
        unreachable(event.data.Item.item.item_type);
      }
    } else {
      unreachable(event.data.Item.event);
    }
  }

  return items;
}

export function updateObjectAt(
  object: GameObjectWithId,
  events: EventEntry[],
  to_t: number
): Particle[] {
  const particles: Particle[] = [];
  for (const event of events) {
    if (
      !enumIs(event.data, 'Object') ||
      event.data.Object.object.id !== object.id
    ) {
      continue;
    }

    (event as any).processed = ((event as any).processed || 0) + 1;

    const data = event.data.Object;

    if (enumIs(data.event, 'GrenadeMoved')) {
      const { from, to } = data.event.GrenadeMoved;
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const dt = event.end_time - event.start_time;

      if (dt == 0) {
        continue;
      }

      const t2 = (to_t - event.start_time) / dt;
      object.object_type.Grenade.loc = {
        x: from.x + dx * t2,
        y: from.y + dy * t2,
      };
    } else if (enumIs(data.event, 'Explosion')) {
      // todo: add explosion particles
      particles.push(
        new ExplosionParticle(
          Date.now(),
          data.event.Explosion.tiles.map(([x, y]) => [Number(x), Number(y)])
        )
      );

      // console.log('Explosion', data.event.Explosion.tiles);
    } else {
      unreachable(data.event);
    }
  }

  return particles;
}

export function updatePlayerAt(
  player: {
    principalText: string;
    health: number;
    maxHealth: number;
    loc: { x: number; y: number; angle: number };
    state: PlayerState;
    weapon: ItemWithId | null;
    moveSpeed: number;
    inventory: Inventory | null;
    armor: number;
    stance: PlayerStance;
    temporaryStatuses: TemporaryStatus[];
    attackSpeed: number;
  },
  events: EventEntry[],
  to_t: number
): Particle[] {
  const particles: Particle[] = [];

  for (const event of events) {
    if (
      !enumIs(event.data, 'Player') ||
      event.data.Player.player.toText() !== player.principalText
    ) {
      continue;
    }

    (event as any).processed = ((event as any).processed || 0) + 1;

    const data = event.data.Player;
    const hasWeapon = player.weapon != null;

    if (enumIs(data.event, 'PlayerMoved')) {
      const { from, to } = data.event.PlayerMoved;
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const dt = event.end_time - event.start_time;

      if (dt == 0) {
        continue;
      }

      const t2 = (to_t - event.start_time) / dt;
      player.loc = {
        x: from.x + dx * t2,
        y: from.y + dy * t2,
        angle: Math.atan2(dy, dx),
      };

      player.health = data.status.health;
      player.maxHealth = data.status.max_health;
      player.moveSpeed = data.status.movement_speed;
      player.state = 'walking';
      player.weapon = data.status.weapon[0];
      player.inventory = data.status.inventory;
      player.armor = data.status.armor;
      player.temporaryStatuses = data.status.temporary_statuses;
      player.attackSpeed = data.status.attack_speed;
      // player.stance = data.status.stance;
    } else if (enumIs(data.event, 'PlayerIdle')) {
      // console.log(e.event, dx, dy, Math.atan2(dy, dx));
      player.health = data.status.health;
      player.maxHealth = data.status.max_health;
      player.moveSpeed = data.status.movement_speed;
      player.loc = {
        x: data.event.PlayerIdle.at.x,
        y: data.event.PlayerIdle.at.y,
        angle: data.event.PlayerIdle.angle,
      };
      player.state = 'idle';
      player.weapon = data.status.weapon[0];
      player.inventory = data.status.inventory;
      player.armor = data.status.armor;
      player.temporaryStatuses = data.status.temporary_statuses;
      player.attackSpeed = data.status.attack_speed;
      // player.stance = data.status.stance;
    } else if (enumIs(data.event, 'PlayerGuarding')) {
      // console.log(e.event, dx, dy, Math.atan2(dy, dx));
      player.health = data.status.health;
      player.maxHealth = data.status.max_health;
      player.moveSpeed = data.status.movement_speed;
      player.loc = {
        x: data.event.PlayerGuarding.at.x,
        y: data.event.PlayerGuarding.at.y,
        angle: data.event.PlayerGuarding.angle,
      };
      player.state = 'guarding';
      player.weapon = data.status.weapon[0];
      player.inventory = data.status.inventory;
      player.armor = data.status.armor;
      player.temporaryStatuses = data.status.temporary_statuses;
      player.attackSpeed = data.status.attack_speed;
      // player.stance = data.status.stance;
    } else if (enumIs(data.event, 'PlayerAttacked')) {
      const { from, at } = data.event.PlayerAttacked;
      player.health = data.status.health;
      player.maxHealth = data.status.max_health;
      player.moveSpeed = data.status.movement_speed;
      player.weapon = data.status.weapon[0];
      player.inventory = data.status.inventory;
      player.armor = data.status.armor;
      player.temporaryStatuses = data.status.temporary_statuses;
      player.attackSpeed = data.status.attack_speed;
      // player.stance = data.status.stance;

      const dx = at.x - from.x;
      const dy = at.y - from.y;
      player.loc.angle = Math.atan2(dy, dx);

      if (enumIs(data.event.PlayerAttacked.weapon, 'Pistol')) {
        particles.push(
          new BulletParticle(
            Date.now(),
            data.event.PlayerAttacked.from,
            data.event.PlayerAttacked.at,
            data.event.PlayerAttacked.hit
          )
        );
      } else if (enumIs(data.event.PlayerAttacked.weapon, 'Knife')) {
        playSound(assets.sounds.slash);
      } else if (enumIs(data.event.PlayerAttacked.weapon, 'Boots')) {
      } else if (enumIs(data.event.PlayerAttacked.weapon, 'MedKit')) {
      } else if (enumIs(data.event.PlayerAttacked.weapon, 'Grenade')) {
      } else if (enumIs(data.event.PlayerAttacked.weapon, 'Armor')) {
      } else if (enumIs(data.event.PlayerAttacked.weapon, 'EnergyDrink')) {
      } else {
        unreachable(data.event.PlayerAttacked.weapon);
      }

      if (!data.event.PlayerAttacked.hit) {
        particles.push(
          new HealthChangeParticle(
            Date.now(),
            'miss',
            data.event.PlayerAttacked.at,
            'white'
          )
        );
      }
    } else if (enumIs(data.event, 'PlayerHealthChange')) {
      const delta = player.health - data.event.PlayerHealthChange.health;
      player.health = data.event.PlayerHealthChange.health;
      player.maxHealth = data.status.max_health;
      player.weapon = data.status.weapon[0];
      player.moveSpeed = data.status.movement_speed;
      player.inventory = data.status.inventory;
      player.armor = data.status.armor;
      player.temporaryStatuses = data.status.temporary_statuses;
      player.attackSpeed = data.status.attack_speed;
      // player.stance = data.status.stance;

      if (player.loc) {
        if (delta > 0) {
          particles.push(
            new HealthChangeParticle(Date.now(), delta, player.loc, 'white')
          );
        } else if (delta < 0) {
          particles.push(
            new HealthChangeParticle(
              Date.now(),
              '+' + delta,
              player.loc,
              'white'
            )
          );
        }
      }

      if (player.health == 0) {
        playSound(assets.sounds.death);
      }
    } else if (enumIs(data.event, 'PlayerDead')) {
      player.health = 0;
      player.loc = {
        ...data.event.PlayerDead.at,
        angle: 0,
      };
    } else {
      unreachable(data.event);
    }

    if (!hasWeapon && player.weapon) {
      player.stance = { Offensive: null };
    }
  }

  if (!player.weapon) {
    player.stance = { Defensive: null };
  }

  // events.

  return particles;
}
